import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Header,
  SpaceBetween,
  ContentLayout,
  Link,
} from "@cloudscape-design/components";
import { ROUTES } from "../../constants";
import { ConfigurableForm } from "../common/Forms";
import { netppmFormSchema } from "./schemas/netppm-form-schema";
import { useFormStore } from "../../store/form-stores";
import { useGlobalStore } from "../../store/global-store";
import { useWorkflowStore } from "../../store/workflow-store";
import { useNotificationStore } from "../../store/notification-store";
import { LoadingScreen } from "../common/UIStates";
import { ConfirmationModal } from "../common/Modals";
import { getCapabilityId } from '../../utils/capabilityMapping';
import { useSessionFormId } from '../../hooks/useSessionFormId';
import { getFirstSelectablePeriod } from '../../utils/PeriodGenerator';
import { NETPPM_STEP_IDS, NETPPM_STEP_NAMES, createNetPPMStepInput } from '../../constants/netppm-step-ids';
import { 
  NETPPM_FAQ_URL,
  DEFAULT_REGION,
  DEFAULT_SCENARIO,
  DEFAULT_DEEP_DIVE,
  REGION_CANADA,
  PLACEHOLDER_SELECT_PRODUCT_FAMILY,
  PLACEHOLDER_SELECT_PRODUCT_FAMILY_FIRST,
  PLACEHOLDER_SELECT_GL_OPTIONAL,
  getProductFamilyGLMapping,
  getProductFamilyOptions,
  NETPPM_PERIOD_TREE_DATA,
  SCENARIO_WEEKLY,
  stripWeekPrefix,
  getPPMPeriodTreeByScenario,
  addWeekPrefix
} from '../../constants/netppm-constants';
import { WORKFLOW_TAGS, getWorkflowDisplayName } from '../../constants/workflow-tags';
import { getNestedLabelByValue, getLabelByValue } from '../../utils/optionUtils';
import { logger } from '../../utils/logger';

export const NetPPMCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  
  // Generate session-specific form ID for this tab
  const [sessionFormId, cleanupSession] = useSessionFormId(netppmFormSchema.id, isEditMode, id);
  
  const { setFormData, getFormData, updateFormField } = useFormStore();
  const { selectedSpaceId, ensureSpaceSelected, fetchSpaces } = useGlobalStore();
  const { workflows, workflowTemplates, fetchWorkflows, fetchWorkflowTemplates, createWorkflow, updateWorkflow } = useWorkflowStore();
  const { error: showError, success: showSuccess } = useNotificationStore();
  
  const [isLoadingWorkflow, setIsLoadingWorkflow] = useState(isEditMode);
  const [cancelModal, setCancelModal] = useState<boolean>(false);
  
  // Fetch spaces and workflow templates on mount
  useEffect(() => {
    fetchSpaces();
  }, [fetchSpaces]);

  useEffect(() => {
    if (selectedSpaceId) {
      fetchWorkflowTemplates();
      if (isEditMode) {
        fetchWorkflows(selectedSpaceId);
      }
    }
  }, [selectedSpaceId, fetchWorkflowTemplates, fetchWorkflows, isEditMode]);

  // Load workflow data for edit mode
  useEffect(() => {
    if (isEditMode && id && workflows.length > 0) {
      const workflow = workflows.find(w => w.id === id);
      if (workflow) {
        populateFormFromWorkflow(workflow);
        setIsLoadingWorkflow(false);
      } else {
        setIsLoadingWorkflow(false);
      }
    }
  }, [isEditMode, id, workflows]);

  // Transform arrays to single values for UI
  const transformFromArrays = (workflowInput: any) => {
    // Helper to extract value from object or return as-is
    const extractValue = (item: any) => {
      if (typeof item === 'object' && item !== null && 'value' in item) {
        return item.value;
      }
      return item;
    };

    return {
      region: (Array.isArray(workflowInput.region) ? extractValue(workflowInput.region[0]) : extractValue(workflowInput.region)) || '',
      product_family: (Array.isArray(workflowInput.product_family) ? extractValue(workflowInput.product_family[0]) : extractValue(workflowInput.product_family)) || '',
      gl_code: Array.isArray(workflowInput.gl_code) ? workflowInput.gl_code.map(extractValue) : (workflowInput.gl_code ? [extractValue(workflowInput.gl_code)] : []),
      deep_dive_dimension: (Array.isArray(workflowInput.deep_dive_dimension) ? extractValue(workflowInput.deep_dive_dimension[0]) : extractValue(workflowInput.deep_dive_dimension)) || '',
      period: (Array.isArray(workflowInput.period) ? extractValue(workflowInput.period[0]) : extractValue(workflowInput.period)) || '',
      scenarios: (Array.isArray(workflowInput.scenarios) ? extractValue(workflowInput.scenarios[0]) : extractValue(workflowInput.scenarios)) || ''
    };
  };

  // Function to populate form from workflow data
  const populateFormFromWorkflow = (workflow: any) => {
    const transformedInput = transformFromArrays(workflow.input || {});
    
    // When GL is empty (PF-level), we save ALL GLs for the Product Family to the API.
    // On load, detect if saved GLs match ALL GLs for PF - if so, show GL as empty.
    const regionGLMapping = getProductFamilyGLMapping(transformedInput.region || DEFAULT_REGION);
    const savedGlCodes = Array.isArray(transformedInput.gl_code) 
      ? transformedInput.gl_code 
      : transformedInput.gl_code ? [transformedInput.gl_code] : [];
    const allGlsForPf = regionGLMapping[transformedInput.product_family]?.map(gl => gl.value) || [];
    const isPfLevel = savedGlCodes.length === allGlsForPf.length && 
      savedGlCodes.every(gl => allGlsForPf.includes(gl));
    
    const formData: any = {
      name: workflow.name || '',
      description: workflow.description || '',
      region: transformedInput.region || DEFAULT_REGION,
      product_family: transformedInput.product_family || '',
      gl_code: isPfLevel ? [] : (transformedInput.gl_code || []),
      deep_dive_dimension: transformedInput.deep_dive_dimension || DEFAULT_DEEP_DIVE,
      period: addWeekPrefix(transformedInput.period, transformedInput.scenarios),
      scenarios: transformedInput.scenarios || DEFAULT_SCENARIO
    };

    setFormData(sessionFormId, formData);
    logger.info('Populated form data from workflow:', { formData });
    
    // Initialize Product Family options for the region
    setDynamicOptions(prev => ({
      ...prev,
      product_family: getProductFamilyOptions(formData.region)
    }));
    
    // Initialize GL field state if Product Family has a value
    if (formData.product_family && regionGLMapping[formData.product_family]) {
      setDynamicOptions(prev => ({
        ...prev,
        gl_code: regionGLMapping[formData.product_family],
        gl_code_disabled: false,
        gl_code_placeholder: PLACEHOLDER_SELECT_GL_OPTIONAL
      }));
    }
    
    // Initialize deep dive options for edit mode
    const { options: deepDiveOptions } = getDeepDiveOptionsWithRestrictions(formData.region, formData.gl_code, formData.scenarios);
    setDynamicOptions(prev => ({ ...prev, deep_dive_dimension: deepDiveOptions }));
  };
  
  // Helper to get deep dive options with restrictions applied
  const getDeepDiveOptionsWithRestrictions = (region: string, glCode: string[], scenario: string) => {
    const restricted = region === REGION_CANADA || !glCode || glCode.length === 0 || scenario !== DEFAULT_SCENARIO;
    logger.info('Deep dive restriction check:', { region, glCode, scenario, restricted });
    
    const deepDiveField = netppmFormSchema.sections.flatMap(s => s.fields).find(f => f.name === 'deep_dive_dimension') as any;
    const baseOptions = deepDiveField?.options || [];
    
    const options = baseOptions.map((opt: any) => ({
      ...opt,
      disabled: restricted && opt.value !== DEFAULT_DEEP_DIVE
    }));
    
    return { options, restricted };
  };
  
  // State for dynamic dropdown options - initialize GL as disabled
  const [dynamicOptions, setDynamicOptions] = useState<Record<string, any>>({
    gl_code_disabled: true,
    gl_code_placeholder: PLACEHOLDER_SELECT_PRODUCT_FAMILY_FIRST,
    product_family: getProductFamilyOptions(DEFAULT_REGION) // Default to US options
  });

  // Initialize field states based on saved form data (for create mode with saved data)
  useEffect(() => {
    if (!isEditMode) {
      const savedData = getFormData(netppmFormSchema.id);
      const region = savedData?.region || DEFAULT_REGION;
      const productFamilyValue = savedData?.product_family;
      const regionGLMapping = getProductFamilyGLMapping(region);
      
      // Initialize Product Family options for the region
      setDynamicOptions(prev => ({
        ...prev,
        product_family: getProductFamilyOptions(region)
      }));
      
      if (productFamilyValue && regionGLMapping[productFamilyValue]) {
        // Enable GL field since Product Family already has value in saved data
        setDynamicOptions(prev => ({
          ...prev,
          gl_code: regionGLMapping[productFamilyValue],
          gl_code_disabled: false,
          gl_code_placeholder: PLACEHOLDER_SELECT_GL_OPTIONAL
        }));
      }
      
      // Initialize deep dive options based on default/saved values
      const glCode = savedData?.gl_code || [];
      const scenario = savedData?.scenarios || DEFAULT_SCENARIO;
      const { options: deepDiveOptions } = getDeepDiveOptionsWithRestrictions(region, glCode, scenario);
      setDynamicOptions(prev => ({ ...prev, deep_dive_dimension: deepDiveOptions }));
    }
  }, [isEditMode, getFormData]);

  // Cleanup form data on component unmount
  useEffect(() => {
    return () => {
      setFormData(sessionFormId, {});
      cleanupSession();
    };
  }, [setFormData, sessionFormId, cleanupSession]);

  // Handle cancellation
  const handleCancel = () => {
    setCancelModal(true);
  };
  
  const handleCancelConfirm = () => {
    // Clear all form data from store
    setFormData(sessionFormId, {});
    cleanupSession();
    navigate(ROUTES.NETPPM_VIEW);
  };
  
  const handleCancelModalClose = () => {
    setCancelModal(false);
  };

  // Handle Save action
  const handleSave = async (data: any) => {
    logger.info("NetPPM form saved:", {
      formData: data,
      timestamp: new Date().toISOString(),
    });

    try {
      const spaceId = await ensureSpaceSelected();
      const regionGLMapping = getProductFamilyGLMapping(data.region);
      
      // Build steps for NetPPM workflow
      const netppmStepIds = [
        NETPPM_STEP_IDS.GENERATE_PRUNED_VARIANCE_TREE,
        NETPPM_STEP_IDS.USER_TREE_SELECTION, 
        NETPPM_STEP_IDS.GENERATE_COMMENTARY
      ];

      // Helper function to get step name
      const getNetPPMStepName = (stepId: string): string => {
        return NETPPM_STEP_NAMES[stepId] || stepId;
      };

      // Get PPM template for capability IDs
      const ppmTemplate = workflowTemplates.find(template => template.tags?.includes(WORKFLOW_TAGS.PPM.value));

      // Create steps with specific inputs
      const steps = netppmStepIds.map(stepId => {
        let stepInput: any;

        stepInput = createNetPPMStepInput[stepId]();

        return {
          id: stepId,
          name: getNetPPMStepName(stepId),
          capabilityId: ppmTemplate ? getCapabilityId(ppmTemplate, stepId) : undefined,
          input: stepInput
        };
      });
      
      // Transform single values to arrays for consistent API format
      const transformToArrays = (formData: any) => {
        return {
          ...formData,
          region: Array.isArray(formData.region) ? formData.region : [formData.region].filter(Boolean),
          product_family: Array.isArray(formData.product_family) ? formData.product_family : [formData.product_family].filter(Boolean),
          gl_code: Array.isArray(formData.gl_code) ? formData.gl_code : [formData.gl_code].filter(Boolean),
          deep_dive_dimension: Array.isArray(formData.deep_dive_dimension) ? formData.deep_dive_dimension : [formData.deep_dive_dimension].filter(Boolean),
          period: Array.isArray(formData.period) ? formData.period : [formData.period].filter(Boolean),
          scenarios: Array.isArray(formData.scenarios) ? formData.scenarios : [formData.scenarios].filter(Boolean)
        };
      };

      // Helper to get field labels and create objects
      const getFieldLabels = (fieldName: string, values: string[]) => {
        if (fieldName === 'gl_code') {
          const productFamily = data.product_family;
          const regionGLMapping = getProductFamilyGLMapping(data.region);
          return values.map(v => getNestedLabelByValue(regionGLMapping, productFamily, v));
        }
        
        const field = netppmFormSchema.sections.flatMap(s => s.fields).find(f => f.name === fieldName) as any;
        
        if (field?.options) {
          return values.map(v => getLabelByValue(field.options, v));
        }
        
        return values;
      };

      const getFieldObjects = (fieldName: string, values: string[]) => {
        const labels = getFieldLabels(fieldName, values);
        return values.map((value, index) => ({ 
          label: labels[index], 
          value: fieldName === 'period' ? stripWeekPrefix(value) : value 
        }));
      };

      // First convert to arrays, then to objects with label+value
      const arrayValues = transformToArrays({
        region: data.region,
        product_family: data.product_family,
        gl_code: data.gl_code,
        deep_dive_dimension: data.deep_dive_dimension,
        period: data.period,
        scenarios: data.scenarios
      });

      // If GL not selected (PF-level), send all GLs for the selected Product Family
      const glCodeValues = arrayValues.gl_code.length > 0 
        ? arrayValues.gl_code 
        : regionGLMapping[data.product_family]?.map(gl => gl.value) || [];
      
      logger.info('GL code values for API:', { selected: arrayValues.gl_code, resolved: glCodeValues });

      const transformedInput = {
        region: getFieldObjects('region', arrayValues.region),
        product_family: getFieldObjects('product_family', arrayValues.product_family),
        gl_code: getFieldObjects('gl_code', glCodeValues),
        deep_dive_dimension: getFieldObjects('deep_dive_dimension', arrayValues.deep_dive_dimension),
        period: getFieldObjects('period', arrayValues.period),
        scenarios: getFieldObjects('scenarios', arrayValues.scenarios)
      };

      // Get reports from PPM template
      const reports = ppmTemplate?.reports || [];

      // Build CreateWorkflowRequest from form data
      const workflowRequest = {
        name: data.name,
        description: data.description,
        input: transformedInput,
        steps: steps,
        reports: reports,
        tags: [WORKFLOW_TAGS.PPM.value]
      };

      // Call the appropriate API based on edit mode
      let response;
      if (isEditMode && id) {
        response = await updateWorkflow(spaceId, id, { 
          spaceId, 
          workflowId: id, 
          workflow: workflowRequest 
        });
      } else {
        response = await createWorkflow({ spaceId, workflow: workflowRequest });
      }
      
      logger.info(`NetPPM workflow ${isEditMode ? 'updated' : 'created'} successfully:`, { response });
      
      // Show success notification
      showSuccess(`${getWorkflowDisplayName(WORKFLOW_TAGS.PPM.value)} Report "${data.name || "Untitled"}" ${isEditMode ? 'updated' : 'saved'} successfully!`, "Success");
      
      // Clear form data and navigate
      setFormData(sessionFormId, {});
      cleanupSession();
      navigate(ROUTES.NETPPM_VIEW);
      
    } catch (error: any) {
      logger.error("Failed to save NetPPM report:", error as Error);
      showError(`Failed to ${isEditMode ? 'update' : 'save'} ${getWorkflowDisplayName(WORKFLOW_TAGS.PPM.value)} Report: ${error?.message || 'Unknown error'}`);
    }
  };

  // Helper to update deep dive dimension options based on restrictions
  const updateDeepDiveOptions = useCallback((region: string, glCode: string[], scenario: string, setValue?: any, currentDeepDive?: string) => {
    const { options, restricted } = getDeepDiveOptionsWithRestrictions(region, glCode, scenario);
    setDynamicOptions(prev => ({ ...prev, deep_dive_dimension: options }));
    
    // Reset to rate_mix if current value is now disabled
    if (restricted && setValue && currentDeepDive && currentDeepDive !== DEFAULT_DEEP_DIVE) {
      logger.info('Resetting deep_dive_dimension to rate_mix:', { previousValue: currentDeepDive });
      setValue('deep_dive_dimension', DEFAULT_DEEP_DIVE);
    }
  }, []);

  // NetPPM-specific field change handler
  const handleFieldChange = useCallback(async (fieldName: string, value: any, setValue?: any, getValues?: any) => {
    logger.info(`NetPPM field "${fieldName}" changed:`, { value });
    
    const values = getValues?.() || {};
    
    // Handle region change - update Product Family options and clear dependent fields
    if (fieldName === 'region') {
      if (setValue) {
        setValue('product_family', null);
        setValue('gl_code', []);
      }
      setDynamicOptions(prev => ({
        ...prev,
        product_family: getProductFamilyOptions(value),
        product_family_placeholder: PLACEHOLDER_SELECT_PRODUCT_FAMILY,
        gl_code: [],
        gl_code_disabled: true,
        gl_code_placeholder: PLACEHOLDER_SELECT_PRODUCT_FAMILY_FIRST
      }));
    }
    
    // Handle NetPPM-specific cascading logic for Product Family
    if (fieldName === 'product_family') {
      const regionGLMapping = getProductFamilyGLMapping(values.region);
      
      // Always clear GL field value first when Product Family changes
      if (setValue) {
        setValue('gl_code', []);
        // Also update the form store to prevent conflicts
        updateFormField(netppmFormSchema.id, 'gl_code', []);
      }
      
      if (value && regionGLMapping[value]) {
        // Enable GL field and populate options based on selected Product Family
        setDynamicOptions(prev => ({
          ...prev,
          gl_code: regionGLMapping[value],
          gl_code_disabled: false,
          gl_code_placeholder: PLACEHOLDER_SELECT_GL_OPTIONAL
        }));
      } else {
        // Disable GL field and clear options when no Product Family is selected
        setDynamicOptions(prev => ({
          ...prev,
          gl_code: [],
          gl_code_disabled: true,
          gl_code_placeholder: PLACEHOLDER_SELECT_PRODUCT_FAMILY_FIRST
        }));
      }
      
      // Update deep dive options (GL cleared = PF level = restricted)
      updateDeepDiveOptions(values.region, [], values.scenarios, setValue, values.deep_dive_dimension);
    }
    
    // Update deep dive options when region, gl_code, or scenarios change
    if (fieldName === 'region' || fieldName === 'gl_code' || fieldName === 'scenarios') {
      const region = fieldName === 'region' ? value : values.region;
      const glCode = fieldName === 'gl_code' ? value : values.gl_code;
      const scenario = fieldName === 'scenarios' ? value : values.scenarios;
      updateDeepDiveOptions(region, glCode, scenario, setValue, values.deep_dive_dimension);
    }
    
    // Swap period tree when scenario changes
    if (fieldName === 'scenarios') {
      const periodTree = getPPMPeriodTreeByScenario(value);
      setDynamicOptions(prev => ({ ...prev, period: periodTree }));
      setValue('period', getFirstSelectablePeriod(periodTree));
    }
  }, [updateFormField, updateDeepDiveOptions]);

  // Get initial form data with default values
  const getInitialValues = () => {
    const savedData = getFormData(sessionFormId);

    // Apply default values from schema
    const defaultValues: any = {};
    netppmFormSchema.sections.forEach((section) => {
      section.fields.forEach((field) => {
        if (field.defaultValue !== undefined) {
          defaultValues[field.name] = field.defaultValue;
        }
      });
    });

    return { ...defaultValues, ...savedData };
  };

  // Show loading screen while fetching workflow data in edit mode
  if (isLoadingWorkflow) {
    return <LoadingScreen message="Loading workflow data..." />;
  }

  return (
    <ContentLayout
      defaultPadding
      header={
        <Header
          variant="h1"
          description={
            <SpaceBetween direction="horizontal" size="xs">
              <span>
                {isEditMode
                  ? `Edit your ${WORKFLOW_TAGS.PPM.label} workflow configuration.`
                  : `Configure your ${WORKFLOW_TAGS.PPM.label} workflow settings below.`
                }
              </span>
              <Link
                href={NETPPM_FAQ_URL}
                external
                fontSize="body-s"
              >
                FAQs
              </Link>
            </SpaceBetween>
          }
        >
          {isEditMode ? `Edit ${WORKFLOW_TAGS.PPM.label} Workflow` : `Create ${WORKFLOW_TAGS.PPM.label} Workflow`}
        </Header>
      }
    >
      <SpaceBetween size="l">
        {/* The Configurable Form with custom action buttons */}
        <ConfigurableForm
          workflowId={sessionFormId}
          schema={netppmFormSchema}
          initialValues={getInitialValues()}
          onFieldChange={handleFieldChange}
          dynamicOptions={dynamicOptions}
          showHeader={false}
          actionButtons={[
            { text: 'Cancel', variant: 'secondary', position: 'left', handler: handleCancel, bypassValidation: true },
            { text: isEditMode ? 'Update' : 'Save', variant: 'primary', position: 'right', handler: handleSave }
          ]}
        />
      </SpaceBetween>

      <ConfirmationModal
        visible={cancelModal}
        title="Confirm Cancellation"
        message="Are you sure you want to cancel? Any unsaved changes will be lost."
        confirmText="Yes, Cancel"
        cancelText="No, Keep Editing"
        variant="primary"
        onConfirm={handleCancelConfirm}
        onCancel={handleCancelModalClose}
      />
    </ContentLayout>
  );
};
